import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import Select, { SingleValue, OnChangeValue } from "react-select";
import { IMAGES } from "../constants/image";
import Charts from "../components/charts/Charts";
import { ViewType } from "../components/charts/chart.types";
import Tooltip from "bootstrap/js/dist/tooltip";
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  AllCommunityModule,
  ColDef,
  ColGroupDef,
} from "ag-grid-community";
import { FaFilter } from "react-icons/fa";
// import { FaDownload} from "react-icons/fa";
import api from "../services/api";
import { ENDPOINTS } from "../services/endpoints";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

ModuleRegistry.registerModules([AllCommunityModule]);

// const DownloadIcon = FaDownload as React.ComponentType<{
//   "aria-hidden"?: boolean;
// }>;
const FilterIcon = FaFilter as React.ComponentType<{ "aria-hidden"?: boolean }>;
interface Option {
  value: string | number;
  label: string;
}
interface DisplayOption {
  label: string;
  value: string;
}
interface CancerApiResponse {
  cancer_master_id: number;
  icc_code: string;
  icc3_code: string;
  cancer_name: string;
  cancer_code: string;
  gender: string;
}
interface DistrictApiResponse {
  district_master_id: number;
  district_code: string;
  district_name: string;
}
interface GenderApi {
  gender_code: string;
  gender_name: string;
}
interface AgeRangeApi {
  from_age_range: number;
  to_age_range: number;
}
interface ResponseTypeApi {
  response_key: string;
  response_value: string;
}
interface RegistryTypeApi{
  registry_key:string;
  registry_name:string;
}
interface VisualModeApi {
  visual_mode_key: string;
  visual_mode_value: string;
}
interface FilterApiResponse {
  gender: GenderApi[];
  year: number[];
  age_group_range: AgeRangeApi[];
  response_type: ResponseTypeApi[];
  registry_type:RegistryTypeApi[];
  visual_mode?: VisualModeApi[];
  default_payload: {
    gender: string;
    from_year: number;
    to_year: number;
    from_age_range: number;
    to_age_range: number;
    response_type: string;
    registry_type:string;
  };
}
interface FilterPayload {
  icc_code: string;
  icc3_code: string;
  district_list: "all" | number[];
  gender: string | null;
  from_year: number | null;
  to_year: number | null;
  from_age_range: number | null;
  to_age_range: number | null;
  response_type: string | null;
  registry_type:string | null;
  visual_mode: string | null;
  page?: number;
  limit?: number;
}
type GridRowData = Record<string, any>;
type HeaderConfig = Record<string, Record<string, string>>;
// const ICON_MAP: Record<string, string> = {
//   bar_chart: IMAGES.DATA_VISUALIZATION.BAR_CHART,
//   pie_chart: IMAGES.DATA_VISUALIZATION.PIE_CHART,
//   table: IMAGES.DATA_VISUALIZATION.TABLE,
// };
interface ChartIcon {
  id: ViewType;
  image: string;
  alt: string;
}
const CHART_ICONS: ChartIcon[] = [
  {
    id: "bar",
    image: IMAGES.DATA_VISUALIZATION.BAR_CHART,
    alt: "Bar Chart ",
  },
  {
    id: "pie",
    image: IMAGES.DATA_VISUALIZATION.PIE_CHART,
    alt: "Pie Chart",
  },
  {
    id: "sunburst",
    image: IMAGES.DATA_VISUALIZATION.SUNBURST_CHART,
    alt: "Sunburst Chart",
  },
  {
    id: "table",
    image: IMAGES.DATA_VISUALIZATION.TABLE,
    alt: "Table",
  },
];
const DataVisualization = () => {
  const gridRef = useRef<AgGridReact<GridRowData>>(null);
  // ADD THESE STATES
  const [columnDefs, setColumnDefs] = useState<(ColDef | ColGroupDef)[]>([]);
  const [rowData, setRowData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedChart, setSelectedChart] = useState<ViewType>("table");
  const [selectedView, setSelectedView] = useState<ViewType>("table");
  const headerMappings: Record<string, HeaderConfig> = {
    population: {
      "ICCC HEAD": {
        "ICCC HEAD": "icc_code",
      },

      DISTRICT: {
        DISTRICT: "district",
      },

      "TOTAL NUMBER OF CASES": {
        "0-4": "0_4_cases",
        "5-9": "5_9_cases",
        "10-14": "10_14_cases",
        "15-19": "15_19_cases",
      },

      TOTAL: {
        TOTAL: "total_cases",
        "%": "cases_percentage",
      },

      "AGE SPECIFIC RATES(0-19)": {
        "0-4": "0_4_aspr",
        "5-9": "5_9_aspr",
        "10-14": "10_14_aspr",
        "15-19": "15_19_aspr",
      },

      "RATES AGE : 0 - 19": {
        "C.I.R": "cir",
        "A.S.R": "asr",
        "CR%": "cr",
      },
    },

    cancer: {
      "ICCC HEAD": {
        "ICCC HEAD": "icc_code",
      },

      ICCC3: {
        ICCC3: "icc3_code",
      },

      "ICCC3 DESCRIPTION": {
        "ICCC3 DESCRIPTION": "description",
      },

      "TOTAL NUMBER OF CASES": {
        "0-4": "0_4_cases",
        "5-9": "5_9_cases",
        "10-14": "10_14_cases",
        "15-19": "15_19_cases",
      },
      TOTAL: {
        TOTAL: "total_cases",
        "%": "cases_percentage",
      },
      "RATES AGE : 0 - 19": {
        "C.I.R": "cir",
        "A.S.R": "asr",
        "CR%": "cr",
      },
    },

    aspr: {
      "ICCC HEAD": {
        "ICCC HEAD": "icc_code",
      },

      ICCC3: {
        ICCC3: "icc3_code",
      },

      "ICCC3 DESCRIPTION": {
        "ICCC3 DESCRIPTION": "description",
      },
      "TOTAL NUMBER OF CASES": {
        "0-4": "0_4_cases",
        "5-9": "5_9_cases",
        "10-14": "10_14_cases",
        "15-19": "15_19_cases",
      },
      TOTAL: {
        TOTAL: "total_cases",
        "%": "cases_percentage",
      },
      "AGE SPECIFIC RATES(0-19)": {
        "0-4": "0_4_aspr",
        "5-9": "5_9_aspr",
        "10-14": "10_14_aspr",
        "15-19": "15_19_aspr",
      },
      "RATES AGE : 0 - 19": {
        "C.I.R": "cir",
        "A.S.R": "asr",
        "CR%": "cr",
      },
    },
  };
  const buildDynamicHeaders = (
    headers: Record<string, string[]>,
    visualMode: string,
  ): (ColDef | ColGroupDef)[] => {
    const normalizedVisualMode = String(visualMode ?? "")
      .toLowerCase()
      .trim();
    const selectedMapping = headerMappings[normalizedVisualMode] || {};
    // Map backend payload keys to desired display labels
    const displayNameMap: Record<string, string> = {
      "ICCC HEAD": "ICCC3 CODE",
      ICCC3: "ICCC3 SUB-TYPE CODE",
    };

    //TOP LEVEL HEADERS SHOULD BE VISIBLE
    const visibilityConfig: Record<string, string[]> = {
      population: [
        "ICCC HEAD",
        "DISTRICT",
        "TOTAL NUMBER OF CASES",
        "TOTAL",
        "RATES AGE : 0 - 19",
      ],

      cancer: [
        "ICCC HEAD",
        "ICCC3",
        "ICCC3 DESCRIPTION",
        "TOTAL NUMBER OF CASES",
        "TOTAL",
        "RATES AGE : 0 - 19",
      ],

      aspr: [
        "ICCC HEAD",
        "ICCC3",
        "ICCC3 DESCRIPTION",
        "TOTAL NUMBER OF CASES",
        "TOTAL",
        "AGE SPECIFIC RATES(0-19)",
        "RATES AGE : 0 - 19",
      ],
    };
    const fixedColumns = [
      "ICCC HEAD",
      "ICCC3",
      "ICCC3 DESCRIPTION",
      "DISTRICT",
    ];
    const visibleHeaders = visibilityConfig[normalizedVisualMode] || [];
    return Object.entries(headers)
      .filter(([groupHeader]) => visibleHeaders.includes(groupHeader))
      .map(([groupHeader, childHeaders]) => {
        const mappedGroup = selectedMapping[groupHeader] || {};
        const displayName = displayNameMap[groupHeader] || groupHeader;
        // =========================
        // SINGLE COLUMN HEADER
        // =========================
        if (Array.isArray(childHeaders) && childHeaders.length === 0) {
          const isPopulationSerialColumn =
            normalizedVisualMode === "population" &&
            groupHeader === "ICCC HEAD";

          return {
            colId: isPopulationSerialColumn
              ? "population_sno"
              : mappedGroup[groupHeader] || groupHeader,
            headerName: isPopulationSerialColumn ? "S.NO" : displayName,
            field: mappedGroup[groupHeader] || "",
            valueGetter: isPopulationSerialColumn
              ? (params) =>
                  params.node?.rowIndex !== null &&
                  params.node?.rowIndex !== undefined
                    ? params.node.rowIndex + 1
                    : ""
              : (params) => params.data?.[params.colDef?.field || ""],
            sortable: true,
            resizable: true,
            width: groupHeader === "ICCC3 DESCRIPTION" ? 250 : 100,
            pinned: fixedColumns.includes(groupHeader) ? "left" : undefined,
          } as ColDef;
        }

        // =========================
        // GROUP HEADER
        // =========================
        return {
          headerName: displayName,
          children: childHeaders
            .map((child: string) => ({
              headerName: child,
              field: mappedGroup[child] || "",
              sortable: true,
              resizable: true,
              width: 130,
            }))
            .filter((column) => column.field),
        } as ColGroupDef;
      });
  };

  const buildTreatmentColumns = (
    headers: Record<string, string[]>,
    visualMode: string,
  ): (ColDef | ColGroupDef)[] => {
    const normalizedVisualMode = String(visualMode ?? "")
      .toLowerCase()
      .trim();
    const scalarFieldAliases: Record<string, string> = {
      district: "district",
      "icc code": "icc_code",
      "iccc code": "icc_code",
      "icc3 code": "icc3_code",
      "iccc3 code": "icc3_code",
      "cancer name": "cancer_name",
    };
    const treatmentFieldAliases: Record<string, string> = {
      "curative treatment given": "curative",
      "no treatment": "no_treatment",
      unknown: "unknown",
      total: "total",
    };
    const toFieldKey = (headerName: string): string =>
      headerName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_|_$/g, "");

    return Object.entries(headers).map(([groupHeader, childHeaders]) => {
      const normalizedGroupHeader = groupHeader.toLowerCase().trim();
      const groupField =
        treatmentFieldAliases[normalizedGroupHeader] ||
        toFieldKey(groupHeader);

      if (childHeaders.length === 0) {
        return {
          field: scalarFieldAliases[normalizedGroupHeader] || toFieldKey(groupHeader),
          headerName: groupHeader,
          sortable: true,
          resizable: true,
          width: 150,
          pinned: normalizedGroupHeader === "district" ? "left" : undefined,
        } as ColDef;
      }

      return {
        headerName: groupHeader,
        children: childHeaders.map((childHeader) => ({
          field:
            childHeader.trim() === "%"
              ? `${groupField}_percentage`
              : `${groupField}_${toFieldKey(childHeader)}`,
          headerName: childHeader,
          sortable: true,
          resizable: true,
          width: 110,
        })),
      } as ColGroupDef;
    });
  };

  const buildTreatmentTypeColumns = (
    headers: Record<string, string[]>,
    visualMode: string,
  ): (ColDef | ColGroupDef)[] => {
    const normalizedVisualMode = String(visualMode ?? "")
      .toLowerCase()
      .trim();
    const isPopulation = normalizedVisualMode === "population";
    const treatmentFieldNames: Record<string, string> = {
      chemotherapy: "chemotherapy",
      radiation: "radiation",
      surgery: "surgery",
      "bone marrow transplant": "bone_marrow_transplant",
    };

    return Object.entries(headers).map(([groupHeader, childHeaders]) => {
      const normalizedGroupHeader = groupHeader
        .replace(/\s*\(n\s*=.*\)$/i, "")
        .trim()
        .toLowerCase();

      if (childHeaders.length === 0) {
        const isClassification =
          normalizedGroupHeader !== "total incidence";

        return {
          field: isClassification
            ? isPopulation
              ? "district_name"
              : "classification"
            : "total_incidence",
          headerName: isClassification
            ? isPopulation
              ? "District"
              : "Iccc3 Extended Classification"
            : groupHeader,
          valueGetter: isClassification
            ? (params) =>
                params.data?.[isPopulation ? "district_name" : "classification"] ??
                params.data?.classification ??
                ""
            : undefined,
          sortable: true,
          resizable: true,
          width: isClassification ? 220 : 150,
          pinned: isClassification ? "left" : undefined,
        } as ColDef;
      }

      const treatmentField = treatmentFieldNames[normalizedGroupHeader];
      return {
        headerName: groupHeader,
        children: childHeaders
          .map((childHeader) => {
            const normalizedChildHeader = childHeader.toLowerCase();
            const field =
              normalizedChildHeader === "%"
                ? `${treatmentField}_percentage`
                : `${treatmentField}_count`;

            return {
              field,
              headerName: childHeader,
              sortable: true,
              resizable: true,
              width: 110,
            } as ColDef;
          })
          .filter(() => Boolean(treatmentField)),
      } as ColGroupDef;
    });
  };

  const buildVitalStatusColumns = (
    headers: Record<string, string[]>,
    visualMode: string,
  ): (ColDef | ColGroupDef)[] => {
    if (visualMode.toLowerCase().trim() === "cancer") {
      const scalarFields: Record<string, string> = {
        "iccc code": "icc_code",
        "iccc3 code": "icc3_code",
        followup: "followup",
        year: "year",
      };

      return Object.entries(headers).map(([groupHeader, childHeaders]) => {
        const normalizedGroupHeader = groupHeader.toLowerCase().trim();

        if (childHeaders.length === 0) {
          const field =
            scalarFields[normalizedGroupHeader] || normalizedGroupHeader;

          return {
            field,
            headerName: groupHeader,
            valueGetter: (params) => params.data?.[field] ?? "",
            sortable: true,
            resizable: true,
            width: normalizedGroupHeader === "year" ? 100 : 150,
            pinned: ["iccc code", "iccc3 code"].includes(
              normalizedGroupHeader,
            )
              ? "left"
              : undefined,
          } as ColDef;
        }

        const statusField = normalizedGroupHeader.replace(/\s+/g, "_");

        return {
          headerName: groupHeader,
          children: childHeaders.map((childHeader) => ({
            field:
              childHeader.trim() === "%"
                ? `${statusField}_percentage`
                : `${statusField}_${childHeader.toLowerCase()}`,
            headerName: childHeader,
            sortable: true,
            resizable: true,
            width: 110,
          })),
        } as ColGroupDef;
      });
    }

    if (visualMode.toLowerCase().trim() === "population") {
      const scalarFields: Record<string, string> = {
        DISTRICT: "district",
        FOLLOWUP: "followup",
        YEAR: "year",
      };

      return Object.entries(headers).map(([groupHeader, childHeaders]) => {
        const normalizedGroupHeader = groupHeader.toLowerCase().trim();

        if (childHeaders.length === 0) {
          const field = scalarFields[normalizedGroupHeader] || normalizedGroupHeader;

          return {
            field,
            headerName: groupHeader,
            valueGetter: (params) => params.data?.[field] ?? "",
            sortable: true,
            resizable: true,
            width: 120,
          } as ColDef;
        }

        const statusField = groupHeader.toLowerCase();
        return {
          headerName: groupHeader,
          children: childHeaders.map((childHeader) => ({
            field:
              childHeader === "%"
                ? `${statusField}_percentage`
                : `${statusField}_${childHeader.toLowerCase()}`,
            headerName: childHeader,
            sortable: true,
            resizable: true,
            width: 110,
          })),
        } as ColGroupDef;
      });
    }

    const frequencyHeader = Object.keys(headers).find((headerName) =>
      /^frequency\b/i.test(headerName.trim()),
    );
    const vitalStatusFields: Record<string, string> = {
      "Vital Status": "vital_status",
      ...(frequencyHeader ? { [frequencyHeader]: "frequency" } : {}),
      "Percentage %": "percentage",
    };

    return Object.keys(vitalStatusFields)
      .filter((headerName) => Object.prototype.hasOwnProperty.call(headers, headerName))
      .map((headerName) => ({
        field: vitalStatusFields[headerName],
        headerName,
        sortable: true,
        resizable: true,
        width: 150,
      }));
  };

  const defaultDisplayOptions: Option[] = [
    { label: "By place", value: "population" },
    { label: "By cancer", value: "cancer" },
    { label: "ASPR", value: "aspr" },
  ];
  const [displayOptions, setDisplayOptions] = useState<Option[]>(defaultDisplayOptions);
  const [selectedCancersType, setSelectedCancersType] = useState<Option[]>([]);
  const [selectedSub_CancersType, setSelectedSub_CancersType] = useState<
    Option[]
  >([]);
  const [genderOptions, setGenderOptions] = useState<Option[]>([]);
  const [districtOptions, setDistrictOptions] = useState<Option[]>([]);
  const [ageFromOptions, setAgeFromOptions] = useState<Option[]>([]);
  const [ageToOptions, setAgeToOptions] = useState<Option[]>([]);
  const [yearOptions, setYearOptions] = useState<Option[]>([]);
  const [responseTypeOptions, setResponseTypeOptions] = useState<Option[]>([]);
  const [registryTypeOptions, setRegistryTypeOptions] = useState<Option[]>([]);
  const [selectedDisplay, setSelectedDisplay] = useState<Option>(defaultDisplayOptions[0]);
  const [cancerTypeOptions, setCancerTypeOptions] = useState<Option[]>([]);
  const [subCancerTypeOptions, setSubCancerTypeOptions] = useState<Option[]>(
    [],
  );
  const [selectedGenders, setSelectedGenders] = useState<Option | null>(null);
  const [selectedFromAge, setSelectedFromAge] = useState<Option | null>(null);
  const [selectedToAge, setSelectedToAge] = useState<Option | null>(null);
  const [selectedFromYear, setSelectedFromYear] = useState<Option | null>(null);
  const [selectedToYear, setSelectedToYear] = useState<Option | null>(null);
  const [selectedResponseType, setSelectedResponseType] =
    useState<Option | null>(null);
  const [selectedRegistryType, setSelectedRegistryType] = useState<Option | null>(null);
  const [selectedDistricts, setSelectedDistricts] = useState<Option[]>([]);
  const [gridKey, setGridKey] = useState(0);
  const prevModeRef = useRef("population_incidence");
  const [showValidationModal, setShowValidationModal] =
    useState<boolean>(false);
  const [validationMessage, setValidationMessage] = useState<string>("");
  const [filterPayload, setFilterPayload] = useState<FilterPayload>({
    icc_code: "all",
    icc3_code: "all",
    district_list: "all",
    gender: "both",
    from_year: 2022,
    to_year: 2023,
    from_age_range: 0,
    to_age_range: 19,
    response_type: "incidence",
    registry_type:"all",
    visual_mode: "population",
    page: 1,
    limit: 25,
  });
  const showErrorModal = (message: string): void => {
    setValidationMessage(message);
    setShowValidationModal(true);
  };
  //-----Filter Handle Change----- 
  const handleDisplayChange = (selectedOption: SingleValue<Option>) => {
    if (!selectedOption) return;

    setSelectedDisplay(selectedOption);
    setCurrentPage(1);

    setFilterPayload((prev) => ({
      ...prev,
      visual_mode: String(selectedOption.value),
      page: 1,
    }));
  };
  const handleCancerTypeChange = (
    selectedOptions: OnChangeValue<Option, true>,
  ) => {
    const selectedCancerOptions = selectedOptions ? [...selectedOptions] : [];

    setSelectedCancersType(selectedCancerOptions);

    const iccCode = selectedCancerOptions
      .map((option) => option.value)
      .join(",");

    setFilterPayload((previousPayload) => ({
      ...previousPayload,
      icc_code: iccCode || "all",

      icc3_code: "all",
    }));

    // Fetch sub-cancer types when icc_code is selected
    if (iccCode && iccCode !== "all") {
      fetchSubCancerTypeOptions(iccCode);
    } else {
      setSelectedSub_CancersType([]);
      setSubCancerTypeOptions([]);
    }
  };

  const fetchSubCancerTypeOptions = async (iccCode: string) => {
    try {
      const response = await api.post(
        ENDPOINTS.DATA_VISUALIZATION.SELECTED_CANCER_TYPE,
        {
          selected_icc_code: iccCode.split(",").map((code) => String(code)),
        },
      );
      const subCancerData = response.data?.icc3_code_list || [];
      const subCancerOpts = subCancerData
        .filter((item: any) => item.icc3_code)
        .map((item: any) => ({
          value: item.icc3_code,
          label: item.cancer_name
            ? `${item.icc3_code} - ${item.cancer_name}`
            : item.icc3_code,
        }));
      setSubCancerTypeOptions(subCancerOpts);
      setSelectedSub_CancersType([]);
    } catch (error) {
      console.error("Error fetching sub-cancer types:", error);
      setSubCancerTypeOptions([]);
    }
  };
  const handleSubCancerTypeChange = (
    selectedOptions: OnChangeValue<Option, true>,
  ) => {
    const selectedOptionsArray = selectedOptions ? [...selectedOptions] : [];
    setSelectedSub_CancersType(selectedOptionsArray);
    const selectedValues = selectedOptionsArray.map((option) => option.value);

    setFilterPayload((previousPayload) => ({
      ...previousPayload,
      // icc3_code: selectedValues.length > 0 ? selectedValues.join(",") : "all",
      // IF NOTHING SELECTED -> PASS ALL
      icc3_code: selectedValues.length > 0 ? selectedValues.join(",") : "all",
    }));
  };
  const handleGenderChange = (selectedOption: SingleValue<Option>) => {
    setSelectedGenders(selectedOption);

    setFilterPayload((previousPayload) => ({
      ...previousPayload,
      gender: selectedOption ? String(selectedOption.value) : null,
    }));
  };
  const handleFromAgeChange = (selectedOption: SingleValue<Option>) => {
    if (!selectedOption) return;
    const fromAge = Number(selectedOption.value);
    const toAge = Number(selectedToAge?.value);
    if (selectedToAge && fromAge > toAge) {
      showErrorModal("From Age should be less than or equal to To Age.");
      return;
    }

    setSelectedFromAge(selectedOption);

    setFilterPayload((previousPayload) => ({
      ...previousPayload,
      from_age_range: fromAge,
    }));
  };
  const handleToAgeChange = (selectedOption: SingleValue<Option>) => {
    if (!selectedOption) return;
    const toAge = Number(selectedOption.value);
    const fromAge = Number(selectedFromAge?.value);
    if (selectedFromAge && toAge < fromAge) {
      showErrorModal("From Age should be less than or equal to To Age.");
      return;
    }

    setSelectedToAge(selectedOption);

    setFilterPayload((previousPayload) => ({
      ...previousPayload,
      to_age_range: toAge,
    }));
  };
  const handleFromYearChange = (selectedOption: SingleValue<Option>) => {
    if (!selectedOption) return;

    const fromYear = Number(selectedOption.value);
    const toYear = Number(selectedToYear?.value);

    if (selectedToYear && fromYear > toYear) {
      showErrorModal("From Year should be less than or equal to To Year.");
      return;
    }

    setSelectedFromYear(selectedOption);

    setFilterPayload((previousPayload) => ({
      ...previousPayload,
      from_year: fromYear,
    }));
  };
  const handleToYearChange = (selectedOption: SingleValue<Option>) => {
    if (!selectedOption) return;

    const toYear = Number(selectedOption.value);
    const fromYear = Number(selectedFromYear?.value);

    if (selectedFromYear && toYear < fromYear) {
      showErrorModal("From Year should be less than or equal to To Year.");
      return;
    }

    setSelectedToYear(selectedOption);

    setFilterPayload((previousPayload) => ({
      ...previousPayload,
      to_year: toYear,
    }));
  };
  // const validateRange = (
  //   fromValue: number,
  //   toValue: number,
  //   fieldName: string,
  // ): boolean => {
  //   if (fromValue > toValue) {
  //     showErrorModal(
  //       `From ${fieldName} should be less than or equal to To ${fieldName}.`,
  //     );
  //     return false;
  //   }

  //   return true;
  // };
  const handleResponseTypeChange = (selectedOption: SingleValue<Option>) => {
    setSelectedResponseType(selectedOption);
    setCurrentPage(1);

    const responseType = String(selectedOption?.value ?? "")
      .toLowerCase()
      .trim();

    if (responseType === "treatment_details") {
      setSelectedChart("table");
      setSelectedView("table");
    } else if (responseType === "treatment_types") {
      setSelectedChart("table");
      setSelectedView("table");
    } else if (responseType === "vital_status") {
      setSelectedChart("table");
      setSelectedView("table");
    }

    setFilterPayload((previousPayload) => ({
      ...previousPayload,
      response_type: selectedOption ? String(selectedOption.value) : null,
      page: 1,
    }));
  };
const handleRegistryTypeChange = (selectedOption: SingleValue<Option>) => {
    setSelectedRegistryType(selectedOption);
    setCurrentPage(1);

    setFilterPayload((previousPayload) => ({
      ...previousPayload,
      registry_type: selectedOption ? String(selectedOption.value) : null,
      page: 1,
    }));
  };
  const handleDistrictChange = (
    selectedOptions: OnChangeValue<Option, true>,
  ) => {
    const selectedDistrictOptions = selectedOptions ? [...selectedOptions] : [];

    setSelectedDistricts(selectedDistrictOptions);
    setFilterPayload((previousPayload) => ({
      ...previousPayload,
      district_list:
        selectedDistrictOptions.length > 0
          ? selectedDistrictOptions
              .map((option) => Number(option.value))
              .filter((value) => !Number.isNaN(value))
          : "all",
    }));
  };

  useEffect(() => {
    if (filterPayload.response_type) {
      fetchAnalyticalData();
    }
  }, [filterPayload]);

  // Reactive hook to sync UI pagination state changes to filterPayload
  useEffect(() => {
    setFilterPayload((prev) => ({
      ...prev,
      page: currentPage,
      limit: pageSize,
    }));
  }, [currentPage, pageSize]);

  const fetchAnalyticalData = async () => {
    try {
      const response = await api.post(
        ENDPOINTS.DATA_VISUALIZATION.GET_ANALYTICAL_DATA,
        filterPayload,
      );
      const responseData = response.data;
      // Keep table rows with chart data so sunburst can build
      // icc_code -> icc3_code -> description hierarchy.
      setChartData({
        ...(responseData.chartData || {}),
        data: responseData.chartData?.data || responseData.data || [],
      });
      if (responseData.response_type === "incidence") {
        // 1. Generate the columns array
        const activeVisualMode = String(
          filterPayload.visual_mode ?? selectedDisplay?.value ?? "population",
        )
          .toLowerCase()
          .trim();

        const generatedCols = buildDynamicHeaders(
          responseData.headers,
          activeVisualMode,
        );

        // 2. Set both column definitions and row data state
        setColumnDefs(generatedCols);
        setRowData(responseData.data || []);
      }
      if (responseData.response_type === "staging") {
        setColumnDefs(buildStagingColumns(responseData.headers));
        setRowData(responseData.data || []);
      }
      if (responseData.response_type === "treatment_details") {
        setColumnDefs(
          buildTreatmentColumns(
            responseData.headers,
            String(
              filterPayload.visual_mode ?? selectedDisplay?.value ?? "",
            ),
          ),
        );
        setRowData(responseData.data || []);
      }
      if (responseData.response_type === "treatment_types") {
        const activeVisualMode = String(
          filterPayload.visual_mode ?? selectedDisplay?.value ?? "population",
        );
        setColumnDefs(
          buildTreatmentTypeColumns(responseData.headers, activeVisualMode),
        );
        setRowData(responseData.data || []);
      }
      if (
        responseData.response_type === "vital_status" &&
        ["population", "cancer"].includes(
          String(filterPayload.visual_mode ?? selectedDisplay?.value ?? "")
            .toLowerCase()
            .trim(),
        )
      ) {
        setColumnDefs(
          buildVitalStatusColumns(
            responseData.headers,
            String(
              filterPayload.visual_mode ?? selectedDisplay?.value ?? "",
            ),
          ),
        );
        setRowData(responseData.data || []);
      }

      // Check if visual mode or response type changed, and force grid remount
      const currentModeKey = `${filterPayload.visual_mode}_${filterPayload.response_type}`;
      if (prevModeRef.current !== currentModeKey) {
        prevModeRef.current = currentModeKey;
        setGridKey((prev) => prev + 1);
      }

      // Update pagination metadata from server response
      setTotalRows(responseData.pagination?.total_rows ?? 0);
      setTotalPages(responseData.pagination?.total_pages ?? 1);
    } catch (error: any) {
      if (error.response) {
        // console.log("Status:", error.response.status);
        // console.log("Data:", error.response.data);
        // console.log("Headers:", error.response.headers);
      } else if (error.request) {
        // console.log("No response received");
      } else {
        // console.log("Axios Error:", error.message);
      }
    }
  };

  // ADD STAGING COLUMN BUILDER
  const buildStagingColumns = useCallback(
    (headers: any): (ColDef | ColGroupDef)[] => {
      // tiers is a dynamic array: [{ key, label, columns }]
      // key   e.g. "tier_1"           — used to look up row data
      // label e.g. "Toronto Tier 1"   — shown as the column group header
      // columns e.g. ["I","II","III"] — individual stage names
      const tiers: any[] = Array.isArray(headers?.tiers) ? headers.tiers : [];

      const normalizeItem = (item: any) => {
        if (typeof item === "string") {
          return { key: item, headerName: item };
        }
        const key =
          item?.field ||
          item?.key ||
          item?.name ||
          item?.headerName ||
          item?.label;
        return {
          key: key ?? String(item),
          headerName:
            item?.headerName || item?.label || item?.name || String(key),
        };
      };

      const tierColumnGroups = tiers.map((tier: any) => {
        const tierKey: string = tier.key; // e.g. "tier_1"
        const tierLabel: string = tier.label; // e.g. "Toronto Tier 1"
        const columns: any[] = Array.isArray(tier.columns) ? tier.columns : [];

        const children = columns.map((item: any) => {
          const { key, headerName } = normalizeItem(item);
          return {
            field: `${tierKey}_${key}`,
            headerName,
            width: 90,
            valueGetter: (params: any) => params.data?.[tierKey]?.[key] ?? 0,
          };
        });

        return {
          headerName: tierLabel,
          children:
            children.length > 0
              ? children
              : [
                  {
                    field: `${tierKey}_empty`,
                    headerName: "No Data",
                    width: 120,
                  },
                ],
        };
      });

      return [
        {
          colId: "staging_icc_code",
          field: "icc_code",
          headerName: "ICCC3 CODE",
          width: 120,
          pinned: "left",
          valueGetter: (params: any) =>
            params.data?.[params.colDef?.field || ""],
        },
        {
          colId: "staging_icc3_code",
          field: "icc3_code",
          headerName: "ICCC3 SUB-TYPE CODE",
          width: 120,
          pinned: "left",
          valueGetter: (params: any) =>
            params.data?.[params.colDef?.field || ""],
        },
        ...tierColumnGroups,
        {
          field: "total_incidence",
          headerName: "Total Incidence",
          width: 150,
        },
      ];
    },
    [],
  );

  const fetchFilter = useCallback(async () => {
    try {
      const [cancerResponse, filterResponse, districtResponse] =
        await Promise.all([
          api.get(ENDPOINTS.DATA_VISUALIZATION.CANCER_LIST),
          api.get(ENDPOINTS.DATA_VISUALIZATION.FILTER),
          api.get(ENDPOINTS.DATA_VISUALIZATION.DISTRICT_LIST),
        ]);

      const allCancer: CancerApiResponse[] = cancerResponse.data.cancer_list;
      const filterData: FilterApiResponse = filterResponse.data.data;
      const allDistricts: DistrictApiResponse[] = districtResponse.data;
      //old
      // const uniqueCancerTypes = Array.from(
      //   new Map(
      //     allCancer.map((item) => [
      //       item.icc_code,
      //       {
      //         value: item.icc_code,
      //         label: `${item.icc_code} - ${item.cancer_name}`,
      //       },
      //     ]),
      //   ).values(),
      // );
      const uniqueCancerTypes = allCancer
        .filter((item) => item.icc_code === item.cancer_code)
        .map((item) => ({
          value: item.icc_code,
          label: `${item.icc_code} - ${item.cancer_name}`,
        }));
      setCancerTypeOptions(uniqueCancerTypes);
      setSubCancerTypeOptions([]);

      const genderOpts = filterData.gender.map((item: GenderApi) => ({
        value: item.gender_code,
        label: item.gender_name,
      }));

      setGenderOptions(genderOpts);

      const fromAgeOpts = filterData.age_group_range.map(
        (item: AgeRangeApi) => ({
          value: item.from_age_range.toString(),
          label: `${item.from_age_range}-${item.to_age_range}`,
        }),
      );

      const toAgeOpts = filterData.age_group_range.map((item: AgeRangeApi) => ({
        value: item.to_age_range.toString(),
        label: `${item.from_age_range}-${item.to_age_range}`,
      }));

      setAgeFromOptions(fromAgeOpts);
      setAgeToOptions(toAgeOpts);

      const yearOpts = filterData.year.map((year: number) => ({
        value: year.toString(),
        label: year.toString(),
      }));

      setYearOptions(yearOpts);

      const responseOpts = filterData.response_type.map(
        (item: ResponseTypeApi) => ({
          value: item.response_key,
          label: item.response_value,
        }),
      );

      setResponseTypeOptions(responseOpts);

      const registryOpts = filterData.registry_type.map(
        (item: RegistryTypeApi) => ({
          value: item.registry_key,
          label: item.registry_name,
        }),
      );
      setRegistryTypeOptions(registryOpts);

      if (Array.isArray(filterData.visual_mode) && filterData.visual_mode.length > 0) {
        const visOpts = filterData.visual_mode.map((item: VisualModeApi) => ({
          value: item.visual_mode_key,
          label: item.visual_mode_value,
        }));
        setDisplayOptions(visOpts);
        const currentVal = selectedDisplay?.value || "population";
        const matched = visOpts.find((o) => o.value === currentVal) || visOpts[0];
        if (matched) {
          setSelectedDisplay(matched);
          setFilterPayload((prev) => ({
            ...prev,
            visual_mode: String(matched.value),
          }));
        }
      }

      const districtOpts = allDistricts.map((item: DistrictApiResponse) => ({
        value: item.district_master_id,
        label: item.district_name,
      }));
      setDistrictOptions(districtOpts);
      setSelectedCancersType([]);
      setSelectedSub_CancersType([]);
      setSelectedGenders({
        value: "both",
        label: genderOpts.find((g) => g.value === "both")?.label || "Both",
      });
      setSelectedFromYear({
        value: "2022",
        label: "2022",
      });
      setSelectedToYear({
        value: "2023",
        label: "2023",
      });
      setSelectedFromAge({
        value: "0",
        label: fromAgeOpts.find((a) => a.value === "0")?.label || "0-4",
      });
      setSelectedToAge({
        value: "19",
        label: toAgeOpts.find((a) => a.value === "19")?.label || "15-19",
      });
      setSelectedResponseType({
        value: "incidence",
        label:
          responseOpts.find((r) => r.value === "incidence")?.label ||
          "Incidence",
      });
      setSelectedRegistryType({
        value: "all",
        label:
          registryOpts.find((r) => r.value === "all")?.label || "All",
      })
      setSelectedDistricts([]);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  }, []);

  useEffect(() => {
    fetchFilter();
  }, [fetchFilter]);

  const defaultColDef = useMemo<ColDef<GridRowData>>(
    () => ({
      sortable: true,
      resizable: true,
      filter: false,
      minWidth: 64,
      wrapHeaderText: true,
      autoHeaderHeight: false,
      cellClass: "data-grid-cell",
    }),
    [],
  );
  const resolvedVisualMode = useMemo(() => {
    const mode = String(
      selectedDisplay?.value ?? filterPayload.visual_mode ?? "population",
    )
      .toLowerCase()
      .trim();

    return mode === "population" || mode === "cancer" || mode === "aspr"
      ? mode
      : "population";
  }, [selectedDisplay?.value, filterPayload.visual_mode]);
  const isStagingSelected = selectedResponseType?.value === "staging";
  const isTreatmentDetailsSelected =
    String(selectedResponseType?.value ?? "").toLowerCase().trim() ===
    "treatment_details";
  const isTreatmentTypesSelected =
    String(selectedResponseType?.value ?? "").toLowerCase().trim() ===
    "treatment_types";
  const isTableOnlyResponseType = ["vital_status"].includes(
    String(selectedResponseType?.value ?? "").toLowerCase().trim(),
  );
  // const handleExportXlsx = async () => {
  //   try {
  //     setIsExporting(true);
  //     const response = await api.post(
  //       ENDPOINTS.DATA_VISUALIZATION.GET_ANALYTICAL_DATA,
  //       filterPayload,
  //       { responseType: "blob" },
  //     );

  //     // Create blob
  //     const blob = new Blob([response.data], {
  //       type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //     });
  //     // Download file
  //     const url = window.URL.createObjectURL(blob);
  //     const link = document.createElement("a");
  //     link.href = url;
  //     // Dynamic file name
  //     const fileName = `Analytics report-${selectedDisplay.value}.xlsx`;
  //     link.setAttribute("download", fileName);

  //     document.body.appendChild(link);

  //     link.click();
  //     link.remove();
  //     window.URL.revokeObjectURL(url);
  //   } catch (error) {
  //     console.error("Excel export failed:", error);
  //   } finally {
  //     setIsExporting(false);
  //   }
  // };
  const handleExportXlsx = async () => {
    try {
      setIsExporting(true);

      const response = await api.post(
        ENDPOINTS.DATA_VISUALIZATION.GET_ANALYTICAL_DATA,
        filterPayload,
        {
          responseType: "blob",
          headers: {
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        },
      );

      // CHECK RESPONSE TYPE
      // console.log(response.headers["content-type"]);

      // CREATE BLOB
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // DOWNLOAD
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      const fileName = `Analytics-report-${selectedDisplay.value}.xlsx`;

      link.download = fileName;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Excel export failed:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const titleData = useMemo(() => {
    const displayLabel = selectedDisplay?.label || "All Display";

    const genderLabel = selectedGenders?.label || "All";

    const fromAgeLabel = selectedFromAge?.label || "All";

    const toAgeLabel = selectedToAge?.label || "All";

    const fromYearLabel = selectedFromYear?.label || "All";

    const toYearLabel = selectedToYear?.label || "All";

    // Visible text
    const cancerDisplayText =
      selectedCancersType.length > 0 ? "Selected" : "All";

    const districtDisplayText =
      selectedDistricts.length > 0 ? "Selected" : "All";
    // Tooltip text
    const cancerTooltip =
      selectedCancersType.length > 0
        ? selectedCancersType.map((opt) => opt.label).join(", ")
        : "All";

    const districtTooltip =
      selectedDistricts.length > 0
        ? selectedDistricts.map((opt) => opt.label).join(", ")
        : "All";
    const cancerLabel =
      selectedCancersType.length > 0
        ? selectedCancersType.map((opt) => opt.label).join(", ")
        : "All Cancer Types";

    const districtLabel =
      selectedDistricts.length > 0
        ? selectedDistricts.map((opt) => opt.label).join(", ")
        : "All Districts";

    return {
      displayLabel,
      genderLabel,
      fromAgeLabel,
      toAgeLabel,
      fromYearLabel,
      toYearLabel,
      cancerLabel,
      districtLabel,
      cancerDisplayText,
      districtDisplayText,
      cancerTooltip,
      districtTooltip,
    };
  }, [
    selectedDisplay,
    selectedGenders,
    selectedFromAge,
    selectedToAge,
    selectedFromYear,
    selectedToYear,
    selectedCancersType,
    selectedDistricts,
  ]);
  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll(
      '[data-bs-toggle="tooltip"]',
    );

    const tooltipInstances = Array.from(tooltipTriggerList).map(
      (el) => new Tooltip(el as HTMLElement),
    );

    return () => {
      tooltipInstances.forEach((tooltip) => tooltip.dispose());
    };
  }, [titleData]);
  const visibleRows = useMemo(() => rowData, [rowData]);

  const pageStart = totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const pageEnd = Math.min(currentPage * pageSize, totalRows);
  const goToPage = (page: number) => {
    setCurrentPage(Math.min(Math.max(page, 1), totalPages));
  };

  return (
    <div className="container-fluid data-visualization-Wrapper py-1">
      <div className="row">
        <div className="col-12">
          <div className="data-visualization-title  mb-1">
            {/* <h6>{titleText}</h6> */}
            <h6 className="visualization-title">
              Frequency of cases, incidence rates, cumulative risk, stage at diagnosis, treatment characteristics, and vital status, stratified  {titleData.displayLabel}, gender is {titleData.genderLabel} and
              agegroup {titleData.fromAgeLabel} to {titleData.toAgeLabel}, for the period {titleData.fromYearLabel} - {titleData.toYearLabel}
              {" | "}
              <span
                className="tooltip-label"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                data-bs-title={titleData.cancerTooltip}
              >
                Cancer Type: {titleData.cancerDisplayText}
              </span>
              {" | "}
              <span
                className="tooltip-label"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                data-bs-title={titleData.districtTooltip}
              >
                Districts: {titleData.districtDisplayText}
              </span>
            </h6>
            {isTreatmentDetailsSelected && selectedView === "table" && (
          <p>
            Please click the Pie Chart to view the reasons for curative treatment not being given.
          </p>
        )}
          </div>
       
        </div>
      </div>
      <div className="row content-layout gx-3">
        <div
          className={`col-12 col-lg-3 filter-column ${
            isFilterOpen ? "filter-column-open" : ""
          }`}
        >
          <div
            id="data-visualization-filter"
            className="Filter-card p-2 rounded-4 shadow-sm"
          >
            <div className="shadow mb-1 bg-white rounded-3 d-flex align-items-center justify-content-evenly">
              {/* <img src={ICON_MAP.bar_chart} alt="Bar Chart" />
              <img src={ICON_MAP.pie_chart} alt="Pie Chart" />
              <img src={ICON_MAP.treemap_chart} alt="Treemap Chart" />
              <img src={ICON_MAP.table} alt="Table" /> */}
              {(isTreatmentDetailsSelected
                ? CHART_ICONS.filter((chart) =>
                    ["table", "pie"].includes(chart.id),
                  )
                : isTreatmentTypesSelected
                  ? CHART_ICONS.filter((chart) =>
                      ["table", "bar", "sunburst"].includes(chart.id),
                    )
                  : isTableOnlyResponseType
                    ? CHART_ICONS.filter((chart) => chart.id === "table")
                    : CHART_ICONS
              ).map((chart) => (
                <img
                  key={chart.id}
                  src={chart.image}
                  alt={chart.alt}
                  role="button"
                  style={{
                    cursor: "pointer",
                    width: 32,
                    height: 32,
                    opacity: selectedChart === chart.id ? 1 : 0.5,
                  }}
                  onClick={() => {
                    setSelectedChart(chart.id);
                    setSelectedView(chart.id);
                  }}
                />
              ))}
            </div>
            <p className="data-visualization-heading mb-1">Cancer Parameters</p>
            <form>
              <div className="mb-1">
                <label className="text-white">Select</label>
                <Select<Option, false>
                  aria-label="Display"
                  closeMenuOnSelect
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select Display"
                  options={displayOptions}
                  value={selectedDisplay}
                  onChange={handleDisplayChange}
                  isDisabled={isStagingSelected}
                />
              </div>
              <div className="mb-1">
                <label className="text-white">Cancer Type</label>
                <Select<Option, true>
                  isMulti
                  aria-label="Cancer Type"
                  className="basic-multi-select"
                  classNamePrefix="select"
                  closeMenuOnSelect
                  placeholder="Cancer Type"
                  options={cancerTypeOptions}
                  value={selectedCancersType}
                  onChange={handleCancerTypeChange}
                />
              </div>
              <div className="mb-1">
                <label className="text-white">Cancer Sub Type</label>
                <Select<Option, true>
                  aria-label=" Cancer Sub Type"
                  isMulti
                  closeMenuOnSelect
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Cancer Sub Type"
                  options={subCancerTypeOptions}
                  value={selectedSub_CancersType}
                  onChange={handleSubCancerTypeChange}
                  isDisabled={selectedCancersType.length === 0}
                />
              </div>
              <div className="mb-1">
                <label className="text-white">Gender</label>
                <Select<Option, false>
                  aria-label="Gender"
                  closeMenuOnSelect
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Gender"
                  options={genderOptions}
                  value={selectedGenders}
                  onChange={handleGenderChange}
                />
              </div>
              <div className="mb-1">
                <label className="text-white">Residence District</label>
                <Select<Option, true>
                  isMulti
                  aria-label="Residence District"
                  closeMenuOnSelect
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="All Districts Together"
                  options={districtOptions}
                  value={selectedDistricts}
                  onChange={handleDistrictChange}
                />
              </div>
              <div className="row gx-2 mb-1">
                <label className="text-white"> Age at Diagnosis</label>
                <div className="col-6">
                  <Select<Option, false>
                    aria-label="Age from"
                    closeMenuOnSelect
                    className="basic-multi-select"
                    classNamePrefix="select"
                    placeholder="From"
                    options={ageFromOptions}
                    value={selectedFromAge}
                    onChange={handleFromAgeChange}
                  />
                </div>
                <div className="col-6">
                  <Select<Option, false>
                    aria-label="Age to"
                    closeMenuOnSelect
                    className="basic-multi-select"
                    classNamePrefix="select"
                    placeholder="To"
                    options={ageToOptions}
                    value={selectedToAge}
                    onChange={handleToAgeChange}
                  />
                </div>
              </div>
              <div className="row gx-2 mb-1">
                <label className="text-white"> Year of Diagnosis</label>
                <div className="col-6">
                  <Select<Option, false>
                    aria-label="Year from"
                    closeMenuOnSelect
                    className="basic-multi-select"
                    classNamePrefix="select"
                    placeholder="From"
                    options={yearOptions}
                    value={selectedFromYear}
                    onChange={handleFromYearChange}
                  />
                </div>
                <div className="col-6">
                  <Select<Option, false>
                    aria-label="Year to"
                    closeMenuOnSelect
                    className="basic-multi-select"
                    classNamePrefix="select"
                    placeholder="To"
                    options={yearOptions}
                    value={selectedToYear}
                    onChange={handleToYearChange}
                  />
                </div>
              </div>
              <div className="mb-1">
                <label className="text-white">Response Type</label>
                <Select<Option, false>
                  aria-label="Response Type"
                  closeMenuOnSelect
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select response"
                  options={responseTypeOptions}
                  value={selectedResponseType}
                  onChange={handleResponseTypeChange}
                />
              </div>
                 <div className="mb-1">
                <label className="text-white">Registry Type</label>
                <Select<Option, false>
                  aria-label="Registry Type"
                  closeMenuOnSelect
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select registry"
                  options={registryTypeOptions}
                  value={selectedRegistryType}
                  onChange={handleRegistryTypeChange}
                />
              </div>
            </form>
          </div>
        </div>

        <div
          className={`col-12 grid-column ${
            isFilterOpen ? "col-lg-9" : "col-lg-12 grid-column-expanded"
          }`}
        >
          <div className="data-visualization-toolbar">
            {/* <button
              type="button"
              className="data-visualization-icon-button"
              onClick={handleExportXlsx}
              disabled={isExporting}
              aria-label={isExporting ? "Exporting data" : "Export data"}
              title={isExporting ? "Exporting data" : "Export data"}
            >
              <DownloadIcon aria-hidden />
            </button> */}
            <button
              type="button"
              className="data-visualization-icon-button filter-toggle-button"
              onClick={() => setIsFilterOpen((prev) => !prev)}
              aria-expanded={isFilterOpen}
              aria-controls="data-visualization-filter"
              aria-label={isFilterOpen ? "Hide filters" : "Show filters"}
              title={isFilterOpen ? "Hide filters" : "Show filters"}
            >
              <FilterIcon aria-hidden />
            </button>
          </div>
          {selectedView === "table" ? (
            <div className="data-visualization-table-shell">
              <div className="ag-theme-alpine custom-grid">
                <AgGridReact
                  key={gridKey}
                  ref={gridRef}
                  theme="legacy"
                  rowData={visibleRows}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  animateRows={true}
                  rowHeight={36}
                  headerHeight={30}
                  groupHeaderHeight={30}
                  getRowClass={(params) => {
                    const rowIndex = params.node.rowIndex ?? 0;

                    return rowIndex % 2 === 0 ? "even-row" : "odd-row";
                  }}
                />
              </div>
              <div className="data-visualization-pagination">
                <div className="rows-page-control">
                  <span>Rows per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    aria-label="Rows per page"
                  >
                    {[25, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  <span>
                    {pageStart}-{pageEnd} of {totalRows}
                  </span>
                </div>
                <div
                  className="pagination-page-control"
                  aria-label="Table pagination"
                >
                  <button
                    type="button"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    &lt;
                  </button>
                  {Array.from(
                    { length: totalPages },
                    (_, index) => index + 1,
                  ).map((page) => (
                    <button
                      key={page}
                      type="button"
                      className={page === currentPage ? "active" : ""}
                      onClick={() => goToPage(page)}
                      aria-label={`Page ${page}`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    &gt;
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <Charts
              displayType={selectedDisplay.value.toString()}
              chartType={selectedView}
              chartData={chartData}
              responseType={selectedResponseType?.value.toString()}
              height="600px"
            />
          )}
        </div>
      </div>
      <div className="data-visualization-notes">
        
        <p>
          Old district boundaries as per 2011 census; Chennai - includes 155
          divisions covering 170 km²
        </p>
    
        <p>
          <strong>Note:</strong> <strong>ASR-</strong>Age Standardized Rate
          (ASR/10^5),
          <strong>CIR-</strong>Crude Incidence Rate (CIR/10^5),
          <strong>CR-</strong>Cumulative Risk% (CR%)
         
        </p>
      </div>
      {showValidationModal && (
        <div className="signup-modal-overlay">
          <div className="signup-modal-box">
            <div className="signup-modal-header">Validation Error</div>

            <div className="signup-modal-body">{validationMessage}</div>

            <div className="signup-modal-footer">
              <button
                type="button"
                className="signup-modal-ok"
                onClick={() => setShowValidationModal(false)}
              >
                Ok
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataVisualization;
